import { useCallback, useMemo, useRef, useState } from "react";

export type DragOperation = "create" | "move" | "resize";

export const SLOT_MINUTES = 15;
export const DAY_START_MINUTES = 8 * 60;
export const DAY_END_MINUTES = 19 * 60;

export interface DragState {
  active: boolean;
  operation: DragOperation | null;
  bookingId: string | null;
  startMinute: number;
  startResourceId: string | null;
  currentMinute: number;
  currentResourceId: string | null;
  originalStartMinute: number | null;
  originalEndMinute: number | null;
  pointerOffsetMinutes: number;
}

export interface DragPreview {
  startMinute: number;
  endMinute: number;
  resourceId: string | null;
}

export interface DragResult {
  operation: DragOperation;
  bookingId: string | null;
  startTime: Date;
  endTime: Date;
  resourceId: string | null;
}

const INITIAL_STATE: DragState = {
  active: false,
  operation: null,
  bookingId: null,
  startMinute: DAY_START_MINUTES,
  startResourceId: null,
  currentMinute: DAY_START_MINUTES,
  currentResourceId: null,
  originalStartMinute: null,
  originalEndMinute: null,
  pointerOffsetMinutes: 0,
};

function clampMinute(minute: number): number {
  return Math.min(Math.max(minute, DAY_START_MINUTES), DAY_END_MINUTES - SLOT_MINUTES);
}

function snapMinute(minute: number): number {
  return clampMinute(Math.floor(minute / SLOT_MINUTES) * SLOT_MINUTES);
}

function createDateAtMinute(baseDate: Date, minuteOfDay: number) {
  const next = new Date(baseDate);
  next.setHours(0, 0, 0, 0);
  next.setMinutes(minuteOfDay, 0, 0);
  return next;
}

export function deriveDragPreview(state: DragState): DragPreview | null {
  if (!state.active || !state.operation) {
    return null;
  }

  if (state.operation === "create") {
    return {
      startMinute: Math.min(state.startMinute, state.currentMinute),
      endMinute: Math.max(state.startMinute, state.currentMinute) + SLOT_MINUTES,
      resourceId: state.startResourceId,
    };
  }

  if (state.operation === "move" && state.originalStartMinute !== null && state.originalEndMinute !== null) {
    const durationMinutes = state.originalEndMinute - state.originalStartMinute;
    const candidateStart = snapMinute(state.currentMinute - state.pointerOffsetMinutes);
    const boundedStart = Math.min(candidateStart, DAY_END_MINUTES - durationMinutes);
    return {
      startMinute: Math.max(boundedStart, DAY_START_MINUTES),
      endMinute: Math.max(boundedStart, DAY_START_MINUTES) + durationMinutes,
      resourceId: state.currentResourceId,
    };
  }

  if (state.operation === "resize" && state.originalStartMinute !== null) {
    const nextEndMinute = Math.max(
      state.originalStartMinute + SLOT_MINUTES,
      Math.min(state.currentMinute + SLOT_MINUTES, DAY_END_MINUTES),
    );

    return {
      startMinute: state.originalStartMinute,
      endMinute: nextEndMinute,
      resourceId: state.startResourceId,
    };
  }

  return null;
}

export function useCalendarDrag(date: Date, onDragComplete: (result: DragResult) => void) {
  const [dragState, setDragState] = useState<DragState>(INITIAL_STATE);
  const dragRef = useRef(INITIAL_STATE);

  const setNextState = useCallback((nextState: DragState) => {
    dragRef.current = nextState;
    setDragState(nextState);
  }, []);

  const startCreateDrag = useCallback((minute: number, resourceId: string | null) => {
    setNextState({
      active: true,
      operation: "create",
      bookingId: null,
      startMinute: snapMinute(minute),
      startResourceId: resourceId,
      currentMinute: snapMinute(minute),
      currentResourceId: resourceId,
      originalStartMinute: null,
      originalEndMinute: null,
      pointerOffsetMinutes: 0,
    });
  }, [setNextState]);

  const startMoveDrag = useCallback((input: {
    bookingId: string;
    resourceId: string | null;
    bookingStartMinute: number;
    bookingEndMinute: number;
    pointerMinute: number;
  }) => {
    setNextState({
      active: true,
      operation: "move",
      bookingId: input.bookingId,
      startMinute: snapMinute(input.pointerMinute),
      startResourceId: input.resourceId,
      currentMinute: snapMinute(input.pointerMinute),
      currentResourceId: input.resourceId,
      originalStartMinute: snapMinute(input.bookingStartMinute),
      originalEndMinute: Math.max(input.bookingEndMinute, input.bookingStartMinute + SLOT_MINUTES),
      pointerOffsetMinutes: snapMinute(input.pointerMinute) - snapMinute(input.bookingStartMinute),
    });
  }, [setNextState]);

  const startResizeDrag = useCallback((input: {
    bookingId: string;
    resourceId: string | null;
    bookingStartMinute: number;
    bookingEndMinute: number;
  }) => {
    const snappedEnd = Math.max(snapMinute(input.bookingEndMinute - SLOT_MINUTES), snapMinute(input.bookingStartMinute));
    setNextState({
      active: true,
      operation: "resize",
      bookingId: input.bookingId,
      startMinute: snappedEnd,
      startResourceId: input.resourceId,
      currentMinute: snappedEnd,
      currentResourceId: input.resourceId,
      originalStartMinute: snapMinute(input.bookingStartMinute),
      originalEndMinute: Math.max(input.bookingEndMinute, input.bookingStartMinute + SLOT_MINUTES),
      pointerOffsetMinutes: 0,
    });
  }, [setNextState]);

  const updateDrag = useCallback((minute: number, resourceId: string | null) => {
    if (!dragRef.current.active) {
      return;
    }

    setNextState({
      ...dragRef.current,
      currentMinute: snapMinute(minute),
      currentResourceId: resourceId,
    });
  }, [setNextState]);

  const cancelDrag = useCallback(() => {
    setNextState(INITIAL_STATE);
  }, [setNextState]);

  const completeDrag = useCallback(() => {
    const preview = deriveDragPreview(dragRef.current);
    const state = dragRef.current;

    if (!preview || !state.operation) {
      setNextState(INITIAL_STATE);
      return;
    }

    onDragComplete({
      operation: state.operation,
      bookingId: state.bookingId,
      startTime: createDateAtMinute(date, preview.startMinute),
      endTime: createDateAtMinute(date, preview.endMinute),
      resourceId: preview.resourceId,
    });

    setNextState(INITIAL_STATE);
  }, [date, onDragComplete, setNextState]);

  const dragPreview = useMemo(() => deriveDragPreview(dragState), [dragState]);

  return {
    dragState,
    dragPreview,
    startCreateDrag,
    startMoveDrag,
    startResizeDrag,
    updateDrag,
    completeDrag,
    cancelDrag,
  };
}
